import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import sortby from 'lodash.sortby';
import classNames from 'classnames';
import styled from 'styled-components';

import AddressAutocomplete from '@atoms/AddressAutocomplete';
import TextInput from '@atoms/TextInput';
import SelectFancy from '@atoms/SelectFancy';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import { ChipButtonLightGray, ErrorText, LabelLarge, BodySmall } from '@styled';
import Asterisk from '@atoms/Asterisk';
import SelectCountries from '@molecules/SelectCountries';
import SelectStatesEcomm from '@molecules/SelectStatesEcomm';
import CheckboxStyled from '@atoms/CheckboxStyled';

const StyledInputContainer = styled.div`
    margin-bottom: 1.5rem;
`;

const AccountNewCompanyFields = ({
    businessTypes,
    isIndependent,
    setIsManualCompanyAddress,
    setHasSecondaryBusinessTypes,
}) => {
    const formik = useFormikContext();
    const [primaryTypeOptions, setPrimaryTypeOptions] = useState();
    const [secondaryTypeOptions, setSecondaryTypeOptions] = useState([]);
    const [selectedBusinessType, setSelectedBusinessType] = useState('');
    const [selectedSecondaryBusinessTypes, setSelectedSecondaryBusinessTypes] = useState([]);
    const [showExpandedAddressFields, setShowExpandedAddressFields] = useState(false);
    const { setFieldValue, values, errors, touched } = formik;

    useEffect(() => {
        if (!businessTypes) {
            return;
        }

        setPrimaryTypeOptions(
            sortby(
                businessTypes.map(businessType => ({ value: businessType.code, label: businessType.description })),
                'label'
            )
        );
    }, [businessTypes]);

    useEffect(() => {
        if (!businessTypes) {
            return;
        }

        if (!selectedBusinessType) {
            setSecondaryTypeOptions([]);
            return;
        }

        const primary = businessTypes.find(businessType => businessType.code === selectedBusinessType);
        const secondaryOptions = primary
            ? sortby(
                primary.children.map(child => ({ value: child.code, label: child.description })),
                'label'
            )
            : [];

        setSecondaryTypeOptions(secondaryOptions);
        setHasSecondaryBusinessTypes(secondaryOptions.length > 0);
    }, [
        businessTypes,
        selectedBusinessType,
        selectedSecondaryBusinessTypes,
        setFieldValue,
        setHasSecondaryBusinessTypes,
    ]);

    useEffect(() => {
        setIsManualCompanyAddress(showExpandedAddressFields);
    }, [setIsManualCompanyAddress, showExpandedAddressFields]);

    const handlePrimaryOptionChange = option => {
        if (!option) {
            setSelectedBusinessType('');
            setFieldValue('businessType', '');
            setSelectedSecondaryBusinessTypes([]);
            setFieldValue('secondaryBusinessTypes', []);
            return;
        }

        setSelectedBusinessType(option.value);
        setFieldValue('businessType', option.value);
        setSelectedSecondaryBusinessTypes([]);
        setFieldValue('secondaryBusinessTypes', []);
    };

    const handleSecondaryOptionChange = option => {
        let listOfSecondaryBusinessTypes;

        if (selectedSecondaryBusinessTypes.includes(option.value)) {
            listOfSecondaryBusinessTypes = selectedSecondaryBusinessTypes.filter(
                businessType => businessType !== option.value
            );
        } else {
            listOfSecondaryBusinessTypes = selectedSecondaryBusinessTypes
                ? [...selectedSecondaryBusinessTypes, option.value]
                : [option.value];
        }

        setSelectedSecondaryBusinessTypes(listOfSecondaryBusinessTypes);
        setFieldValue('secondaryBusinessTypes', listOfSecondaryBusinessTypes);
    };

    if (!primaryTypeOptions) {
        return <p>Loading...</p>;
    }

    return (
        <div>
            {!isIndependent && (
                <>
                    <StyledInputContainer>
                        <TextInput label="New Company Name" name="newCompanyName" required type="text" />
                    </StyledInputContainer>
                    <StyledInputContainer>
                        <SelectFancy
                            label="Business Type"
                            name="businessType"
                            options={primaryTypeOptions}
                            onChange={option => handlePrimaryOptionChange(option)}
                            required
                        />
                    </StyledInputContainer>
                    {selectedBusinessType === 'RET' && (
                        <>
                            <BodySmall className='mb-3'>
                                Please confirm your company's primary business is the direct retail sale of goods or services to individual consumers. If so, your business category is retailer or tenant. (Investors and client service providers who are not directly involved in retail or consumer service operations do not qualify in this category.)
                            </BodySmall>
                            <CheckboxStyled
                                name="retailerAcknowledged"
                                label="By checking this box, I confirm that my company's primary business is Retailer/Tenant"
                                required
                            />
                        </>
                    )}
                    {secondaryTypeOptions.length > 0 && (
                        <StyledInputContainer className="form-group">
                            <div className="mb-2">
                                <LabelLarge>Secondary Business Type</LabelLarge>
                                <Asterisk />
                            </div>
                            <div className="d-flex flex-wrap">
                                {secondaryTypeOptions.map(secondaryOption => (
                                    <ChipButtonLightGray
                                        type="button"
                                        onClick={() => handleSecondaryOptionChange(secondaryOption)}
                                        key={secondaryOption.value}
                                        className={classNames({
                                            active: values?.secondaryBusinessTypes.includes(secondaryOption.value),
                                        })}
                                    >
                                        {secondaryOption.label}
                                    </ChipButtonLightGray>
                                ))}
                            </div>
                            {touched?.secondaryBusinessTypes && errors?.secondaryBusinessTypes ? (
                                <ErrorText>{errors.secondaryBusinessTypes}</ErrorText>
                            ) : null}
                        </StyledInputContainer>
                    )}
                </>
            )}
            <StyledInputContainer>
                <AddressAutocomplete
                    label="Address"
                    name="companyAddressFull"
                    placeholder="Start typing an address..."
                    required
                    styles={{
                        zIndex: 'auto',
                    }}
                    onAddressSelect={address => {
                        setFieldValue('companyAddressAddress1', address.address1);
                        setFieldValue('companyAddressAddress2', address.address2 || '');
                        setFieldValue('companyAddressAddress3', address.address3 || '');
                        setFieldValue('companyAddressCity', address.city);
                        setFieldValue('companyAddressState', address.state);
                        setFieldValue('companyAddressCountry', address.country);
                        setFieldValue('companyAddressZip', address.zip);
                    }}
                    onAddressClear={() => {
                        setFieldValue('companyAddressAddress1', '');
                        setFieldValue('companyAddressAddress2', '');
                        setFieldValue('companyAddressAddress3', '');
                        setFieldValue('companyAddressCity', '');
                        setFieldValue('companyAddressState', '');
                        setFieldValue('companyAddressCountry', '');
                        setFieldValue('companyAddressZip', '');
                    }}
                    onShowExpandedAddressFields={value => {
                        setShowExpandedAddressFields(value);
                    }}
                    showEditAddress
                />
                {showExpandedAddressFields && (
                    <>
                        <StyledInputContainer className="one-per-row">
                            <SelectCountries name="companyAddressCountry" label="Select Your Country" required />
                        </StyledInputContainer>
                        <StyledInputContainer className="one-per-row">
                            <TextInput label="Address Line 1" name="companyAddressAddress1" type="text" required />
                        </StyledInputContainer>
                        <StyledInputContainer className="one-per-row">
                            <TextInput label="Address Line 2" name="companyAddressAddress2" type="text" />
                        </StyledInputContainer>
                        <StyledInputContainer className="one-per-row">
                            <TextInput label="Address Line 3" name="companyAddressAddress3" type="text" />
                        </StyledInputContainer>
                        <StyledInputContainer className="one-per-row">
                            <TextInput label="City" name="companyAddressCity" type="text" required />
                        </StyledInputContainer>
                        <StyledInputContainer className="one-per-row">
                            <TextInput label="Zip/Postal Code" name="companyAddressZip" type="text" required />
                        </StyledInputContainer>
                        <StyledInputContainer className="one-per-row">
                            <SelectStatesEcomm
                                name="companyAddressState"
                                countryFieldName="companyAddressCountry"
                                required
                            />
                        </StyledInputContainer>
                    </>
                )}
            </StyledInputContainer>
            <StyledInputContainer>
                <CheckboxWithoutForm
                    checked={values.minorityOwned}
                    onCheckChange={checked => setFieldValue('minorityOwned', checked)}
                >
                    Minority-Owned Business
                </CheckboxWithoutForm>
            </StyledInputContainer>
            <StyledInputContainer>
                <CheckboxWithoutForm
                    checked={values.womanOwned}
                    onCheckChange={checked => setFieldValue('womanOwned', checked)}
                >
                    Women-Owned Business
                </CheckboxWithoutForm>
            </StyledInputContainer>
            <StyledInputContainer>
                <TextInput label="Job Title" name="title" required type="text" />
            </StyledInputContainer>
        </div>
    );
};

AccountNewCompanyFields.propTypes = {
    businessTypes: PropTypes.array,
    isIndependent: PropTypes.bool.isRequired,
    setIsManualCompanyAddress: PropTypes.func,
    setHasSecondaryBusinessTypes: PropTypes.func,
};

export default AccountNewCompanyFields;
